import React, { useState, useMemo } from 'react';
import { Layout, ShoppingBag, BookOpen, BarChart3 } from './icons';
import type { RGB } from '../types';
import { rgbToHex, rgbToHsl, hslToRgb } from '../utils/colors';
import LandingPreview from './PreviewLayouts/LandingPreview';
import DashboardPreview from './PreviewLayouts/DashboardPreview';
import BlogPreview from './PreviewLayouts/BlogPreview';
import EcommercePreview from './PreviewLayouts/EcommercePreview';

export interface PreviewColorScheme {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  textMuted: string;
  border: string;
}

type LayoutType = 'landing' | 'dashboard' | 'blog' | 'ecommerce';

interface WebsitePreviewProps {
  harmonyColors: RGB[];
}

/** Derive a full 8-role scheme from harmony palette (5 colors). */
const deriveSchemeFromHarmony = (colors: RGB[]): PreviewColorScheme => {
  const primary = colors[0];
  const secondary = colors[1] || primary;
  const accent = colors[2] || secondary;
  const borderSource = colors[3] || primary;

  const hexPrimary = rgbToHex(primary.r, primary.g, primary.b);
  const hexSecondary = rgbToHex(secondary.r, secondary.g, secondary.b);
  const hexAccent = rgbToHex(accent.r, accent.g, accent.b);

  const hslPrimary = rgbToHsl(primary.r, primary.g, primary.b);
  const hslBorder = rgbToHsl(borderSource.r, borderSource.g, borderSource.b);

  // Background: very light tint of primary
  const bgRgb = hslToRgb(hslPrimary.h, Math.min(hslPrimary.s, 15), 97);
  const bg = rgbToHex(bgRgb.r, bgRgb.g, bgRgb.b);

  // Surface: near-white
  const surfRgb = hslToRgb(hslPrimary.h, Math.min(hslPrimary.s, 10), 100);
  const surface = rgbToHex(surfRgb.r, surfRgb.g, surfRgb.b);

  // Text: dark shade from primary hue
  const textRgb = hslToRgb(hslPrimary.h, Math.min(hslPrimary.s, 20), 15);
  const text = rgbToHex(textRgb.r, textRgb.g, textRgb.b);

  // Text muted
  const mutedRgb = hslToRgb(hslPrimary.h, Math.min(hslPrimary.s, 10), 50);
  const textMuted = rgbToHex(mutedRgb.r, mutedRgb.g, mutedRgb.b);

  // Border: light tint from 4th harmony color
  const borderRgb = hslToRgb(hslBorder.h, Math.min(hslBorder.s, 12), 88);
  const border = rgbToHex(borderRgb.r, borderRgb.g, borderRgb.b);

  return {
    primary: hexPrimary,
    secondary: hexSecondary,
    accent: hexAccent,
    background: bg,
    surface,
    text,
    textMuted,
    border,
  };
};

/** Fallback: derive from single color (legacy behavior). */
const deriveSchemeFromSingle = (color: RGB): PreviewColorScheme => {
  const hexA = rgbToHex(color.r, color.g, color.b);
  const hslA = rgbToHsl(color.r, color.g, color.b);

  const compH = (hslA.h + 180) % 360;
  const compRgb = hslToRgb(compH, hslA.s, hslA.l);
  const hexB = rgbToHex(compRgb.r, compRgb.g, compRgb.b);
  const hslB = rgbToHsl(compRgb.r, compRgb.g, compRgb.b);

  const bgRgb = hslToRgb(hslA.h, Math.min(hslA.s, 15), 97);
  const surfRgb = hslToRgb(hslA.h, Math.min(hslA.s, 10), 100);
  const textRgb = hslToRgb(hslA.h, Math.min(hslA.s, 20), 15);
  const mutedRgb = hslToRgb(hslA.h, Math.min(hslA.s, 10), 50);
  const borderRgb = hslToRgb(hslA.h, Math.min(hslA.s, 10), 88);
  const accentRgb = hslToRgb(hslB.h, Math.min(hslB.s + 10, 100), Math.min(hslB.l + 5, 65));

  return {
    primary: hexA,
    secondary: hexB,
    accent: rgbToHex(accentRgb.r, accentRgb.g, accentRgb.b),
    background: rgbToHex(bgRgb.r, bgRgb.g, bgRgb.b),
    surface: rgbToHex(surfRgb.r, surfRgb.g, surfRgb.b),
    text: rgbToHex(textRgb.r, textRgb.g, textRgb.b),
    textMuted: rgbToHex(mutedRgb.r, mutedRgb.g, mutedRgb.b),
    border: rgbToHex(borderRgb.r, borderRgb.g, borderRgb.b),
  };
};

const LAYOUTS: { type: LayoutType; label: string; icon: React.ElementType }[] = [
  { type: 'landing', label: 'Landing', icon: Layout },
  { type: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { type: 'blog', label: 'Blog', icon: BookOpen },
  { type: 'ecommerce', label: 'Shop', icon: ShoppingBag },
];

const WebsitePreview: React.FC<WebsitePreviewProps> = ({ harmonyColors }) => {
  const [activeLayout, setActiveLayout] = useState<LayoutType>('landing');

  const scheme = useMemo(() => {
    if (harmonyColors.length >= 3) {
      return deriveSchemeFromHarmony(harmonyColors);
    }
    // Fallback: use first color or default
    const fallback = harmonyColors[0] || { r: 99, g: 102, b: 241 };
    return deriveSchemeFromSingle(fallback);
  }, [harmonyColors]);

  const PreviewComponent = {
    landing: LandingPreview,
    dashboard: DashboardPreview,
    blog: BlogPreview,
    ecommerce: EcommercePreview,
  }[activeLayout];

  return (
    <>
      <div className="flex items-center justify-end gap-3 mb-3 md:mb-5">
        <div className="flex gap-1 bg-surface-sunken rounded-lg p-1">
          {LAYOUTS.map(({ type, label, icon: Icon }) => (
            <button
              key={type}
              onClick={() => setActiveLayout(type)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                activeLayout === type
                  ? 'bg-white text-ink shadow-sm'
                  : 'text-ink-secondary hover:text-ink-secondary'
              }`}
            >
              <Icon size={14} />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Preview Container */}
      <div
        className="rounded-lg border border-surface-border overflow-hidden h-[240px] md:h-auto"
        style={{ backgroundColor: scheme.background }}
      >
        <div className="transform scale-[0.6] origin-top-left md:scale-100 md:origin-top">
          <div className="w-[167%] md:w-full">
            <PreviewComponent scheme={scheme} />
          </div>
        </div>
      </div>

      {/* Color Mapping Legend */}
      <div className="mt-4 hidden md:flex flex-wrap gap-3">
        {[
          { label: 'Primary', color: scheme.primary },
          { label: 'Secondary', color: scheme.secondary },
          { label: 'Accent', color: scheme.accent },
          { label: 'Background', color: scheme.background },
          { label: 'Text', color: scheme.text },
          { label: 'Border', color: scheme.border },
        ].map(({ label, color }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-sm border border-surface-border-strong"
              style={{ backgroundColor: color }}
            />
            <span className="text-xs text-ink-secondary">{label}</span>
          </div>
        ))}
      </div>
    </>
  );
};

export default WebsitePreview;
